import { useMemo, useState } from 'react';
import {
  Folder,
  FolderOpen,
  Plus,
  Star,
  Image as ImageIcon,
  Layers,
  Trash2,
  ChevronRight,
  ChevronDown,
  FolderPlus,
} from 'lucide-react';
import { useAssetStore } from '../../../stores/assetStore';
import type { AssetFolder } from '../../../types';

interface FolderNode {
  folder: AssetFolder;
  children: FolderNode[];
}

function buildTree(folders: AssetFolder[]): FolderNode[] {
  const byId = new Map<string, FolderNode>();
  folders.forEach((f) => byId.set(f.id, { folder: f, children: [] }));
  const roots: FolderNode[] = [];
  folders.forEach((f) => {
    const node = byId.get(f.id)!;
    if (f.parent_id && byId.has(f.parent_id)) {
      byId.get(f.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

export default function AssetFolderList() {
  const {
    folders,
    assets,
    activeFolderId,
    favoritesOnly,
    canDelete,
    setActiveFolderId,
    setFavoritesOnly,
    createFolder,
    deleteFolder,
  } = useAssetStore();

  const [creatingParentId, setCreatingParentId] = useState<string | null | undefined>(undefined);
  const [newName, setNewName] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(folders.map((f) => f.id)));

  const tree = useMemo(() => buildTree(folders), [folders]);

  const allCount = assets.length;
  const favCount = assets.filter((a) => a.is_favorite).length;

  const handleCreate = async () => {
    const name = newName.trim();
    const parent = creatingParentId ?? null;
    if (!name) {
      setCreatingParentId(undefined);
      setNewName('');
      return;
    }
    const created = await createFolder(name, parent);
    if (created && parent) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(parent);
        return next;
      });
    }
    setNewName('');
    setCreatingParentId(undefined);
  };

  const cancelCreate = () => {
    setCreatingParentId(undefined);
    setNewName('');
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const countFor = (id: string) => assets.filter((a) => a.folder_id === id).length;

  const renderNode = (node: FolderNode, depth: number) => {
    const { folder, children } = node;
    const active = activeFolderId === folder.id && !favoritesOnly;
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(folder.id);
    const isCreatingHere = creatingParentId === folder.id;

    return (
      <div key={folder.id}>
        <div className="group relative flex items-center">
          <button
            onClick={() => {
              if (hasChildren) toggleExpand(folder.id);
            }}
            className={`flex items-center justify-center w-4 h-6 text-zinc-600 hover:text-zinc-300 transition-colors ${
              hasChildren ? '' : 'invisible'
            }`}
            style={{ marginLeft: depth * 10 }}
            tabIndex={-1}
          >
            {isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          </button>
          <button
            onClick={() => {
              setActiveFolderId(folder.id);
              setFavoritesOnly(false);
            }}
            className={`flex-1 flex items-center gap-2 px-2 py-1.5 rounded text-[11px] transition-colors ${
              active ? 'bg-cyan-500/10 text-cyan-300' : 'text-zinc-400 hover:bg-white/[0.04]'
            }`}
          >
            {active ? (
              <FolderOpen size={12} style={{ color: folder.color }} />
            ) : (
              <Folder size={12} style={{ color: folder.color }} />
            )}
            <span className="flex-1 text-left truncate">{folder.name}</span>
            <span className="text-[10px] text-zinc-600">{countFor(folder.id)}</span>
          </button>
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-[#141414]/90 rounded px-0.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCreatingParentId(folder.id);
                setNewName('');
              }}
              className="p-0.5 text-zinc-500 hover:text-cyan-400"
              title="New subfolder"
            >
              <FolderPlus size={10} />
            </button>
            {canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    confirm(
                      `Delete folder "${folder.name}"? Assets inside will be unassigned and subfolders will be orphaned.`
                    )
                  ) {
                    deleteFolder(folder.id);
                  }
                }}
                className="p-0.5 text-zinc-500 hover:text-red-400"
                title="Delete folder"
              >
                <Trash2 size={10} />
              </button>
            )}
          </div>
        </div>

        {isCreatingHere && (
          <div className="px-2 py-1" style={{ paddingLeft: (depth + 1) * 10 + 14 }}>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={handleCreate}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') {
                  setCreatingParentId(undefined);
                  setNewName('');
                }
              }}
              placeholder="Subfolder name"
              className="w-full px-2 py-1 bg-white/[0.04] border border-cyan-500/30 rounded text-[11px] text-zinc-200 placeholder-zinc-600 focus:outline-none"
            />
          </div>
        )}

        {hasChildren && isExpanded && (
          <div>{children.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="px-2 py-2 space-y-0.5 border-b border-white/[0.06]">
      <button
        onClick={() => {
          setActiveFolderId(null);
          setFavoritesOnly(false);
        }}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] transition-colors ${
          !activeFolderId && !favoritesOnly
            ? 'bg-cyan-500/10 text-cyan-300'
            : 'text-zinc-400 hover:bg-white/[0.04]'
        }`}
      >
        <Layers size={12} />
        <span className="flex-1 text-left">All Assets</span>
        <span className="text-[10px] text-zinc-600">{allCount}</span>
      </button>

      <button
        onClick={() => {
          setFavoritesOnly(!favoritesOnly);
          setActiveFolderId(null);
        }}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] transition-colors ${
          favoritesOnly
            ? 'bg-amber-500/10 text-amber-300'
            : 'text-zinc-400 hover:bg-white/[0.04]'
        }`}
      >
        <Star size={12} className={favoritesOnly ? 'fill-amber-400' : ''} />
        <span className="flex-1 text-left">Favorites</span>
        <span className="text-[10px] text-zinc-600">{favCount}</span>
      </button>

      <div className="pt-2 pb-1 flex items-center justify-between px-2">
        <span className="text-[9px] text-zinc-600 uppercase tracking-wider font-medium">
          Folders
        </span>
        <button
          onClick={() => {
            setCreatingParentId(null);
            setNewName('');
          }}
          className="text-zinc-500 hover:text-cyan-400 transition-colors"
          title="New root folder"
        >
          <Plus size={12} />
        </button>
      </div>

      {creatingParentId === null && (
        <div className="px-2 py-1">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={handleCreate}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') cancelCreate();
            }}
            placeholder="Folder name"
            className="w-full px-2 py-1 bg-white/[0.04] border border-cyan-500/30 rounded text-[11px] text-zinc-200 placeholder-zinc-600 focus:outline-none"
          />
        </div>
      )}

      {tree.map((node) => renderNode(node, 0))}

      {folders.length === 0 && (
        <div className="px-2 py-3 text-center text-[10px] text-zinc-600 flex flex-col items-center gap-1">
          <ImageIcon size={14} className="opacity-50" />
          <span>No folders yet</span>
        </div>
      )}
    </div>
  );
}

